import { useId, useRef, useState, type KeyboardEvent } from 'react';
import type { TrackNoteItem } from './TrackNotes';

export function TrackDetailNotes({ items }: { items: TrackNoteItem[] }) {
    const id = useId();
    const buttons = useRef<Array<HTMLButtonElement | null>>([]);
    const [selectedKey, setSelectedKey] = useState('narrative');
    const narrative = items.filter((item) => item.key !== 'production');
    const production = items.filter((item) => item.key === 'production');
    const sections = [
        { key: 'narrative', label: narrative.some((item) => item.key === 'narrative') ? 'Narrative' : 'Overview', items: narrative },
        { key: 'production', label: 'Production', items: production },
    ].filter((section) => section.items.length > 0);
    const selected = sections.find((section) => section.key === selectedKey) ?? sections[0];

    if (!selected) return null;

    function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
        let next: number;
        switch (event.key) {
            case 'ArrowRight': next = (index + 1) % sections.length; break;
            case 'ArrowLeft': next = (index + sections.length - 1) % sections.length; break;
            case 'Home': next = 0; break;
            case 'End': next = sections.length - 1; break;
            default: return;
        }
        event.preventDefault();
        setSelectedKey(sections[next].key);
        buttons.current[next]?.focus();
    }

    return (
        <div className="track-detail-notes">
            {sections.length > 1 ? (
                <div className="track-detail-notes__tabs" role="tablist" aria-label="Track notes">
                    {sections.map((section, index) => (
                        <button
                            key={section.key}
                            ref={(element) => { buttons.current[index] = element; }}
                            id={`${id}-${section.key}-tab`}
                            type="button"
                            role="tab"
                            aria-selected={section.key === selected.key}
                            aria-controls={`${id}-${section.key}-panel`}
                            tabIndex={section.key === selected.key ? 0 : -1}
                            onClick={() => setSelectedKey(section.key)}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                        >
                            {section.label}
                        </button>
                    ))}
                </div>
            ) : (
                <h3 className="track-detail-notes__label" id={`${id}-${selected.key}-tab`}>{selected.label}</h3>
            )}
            {sections.map((section) => (
                <div
                    key={section.key}
                    id={`${id}-${section.key}-panel`}
                    className="track-detail-notes__body"
                    role={sections.length > 1 ? 'tabpanel' : 'region'}
                    aria-labelledby={`${id}-${section.key}-tab`}
                    hidden={section.key !== selected.key}
                    tabIndex={0}
                >
                    {section.items.map((item) => (
                        <section key={item.key} className="track-detail-notes__section">
                            {section.items.length > 1 ? <h3>{item.label}</h3> : null}
                            <p>{item.text}</p>
                        </section>
                    ))}
                </div>
            ))}
        </div>
    );
}
